import { Alert, AlertTitle, Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import ZscalerZpaConnectForm from "./ZscalerZpaConnectForm";
import zscaler_zpa_step_1 from "./images/zscaler_zpa_step_1.png";
import zscaler_zpa_step_2 from "./images/zscaler_zpa_step_2.png";
import zscaler_zpa_step_3 from "./images/zscaler_zpa_step_3.png";
import zscaler_zpa_step_4 from "./images/zscaler_zpa_step_4.png";
import zscaler_zpa_step_5 from "./images/zscaler_zpa_step_5.png";
import zscaler_zpa_step_6 from "./images/zscaler_zpa_step_6.png";

const ZscalerZpaIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const zscalerZpaLoginURL = "https://admin.zscaler.net/";
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("zscaler_zpa");
  const [loading, setLoading] = useState(false);
  const { registerZscalerZpaIntegration } = useAPI();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const onSubmit = (values) => {
    setLoading(true);
    registerZscalerZpaIntegration(
      values.clientId,
      values.secret,
      values.zpa_customer_id
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Zscaler ZPA integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Zscaler ZPA and prioritize the suggested remediations."
            : ""
        }
        image={logos.ZscalerLogo}
        name={"Zscaler ZPA"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Zscaler ZPA is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* DISCLAIMER */}
              <Box mb="20px">
                <Alert severity="info">
                  <AlertTitle>Disclaimer</AlertTitle>
                  Zscaler ZPA currently provides only an API key with both read
                  and write permissions. Breeze, however, uses the API key
                  solely for read permissions to collect configuration
                  information. The API key is secured with encryption, access
                  restrictions, and continuous monitoring by security
                  professionals, along with oversight from external auditing
                  firms.
                </Alert>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Zscaler ZPA">
                  <Typography variant={"body2"}>
                    Login to your Zscaler ZPA account
                  </Typography>
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(zscalerZpaLoginURL, "_blank");
                      }}
                    >
                      Login to Zscaler ZPA Account
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Add a new API key">
                  <Typography variant={"body2"}>
                    Go to Configuration & Control {">"} Public API {">"} API
                    Keys.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zpa_step_1,
                        altext: "Zscaler ZPA API Keys Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>Click Add API Key.</Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zpa_step_2,
                        altext: "Zscaler ZPA add API Key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    In the Add API Key window:
                  </Typography>
                  <Typography variant={"body2"}>
                    1. Name: Enter a name for the key.
                  </Typography>
                  <Typography variant={"body2"}>
                    2. Status: Enable the key.
                  </Typography>
                  <Typography variant={"body2"}>
                    3. Session Validity Interval (In Seconds): The maximum
                    amount of time is 3600 seconds.
                  </Typography>
                  <Typography variant={"body2"}>4. Click Save.</Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zpa_step_3,
                        altext: "Zscaler ZPA new API Key form",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    The client secret for the key displays. Copy the client
                    secret to your clipboard. And paste it in the form below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zpa_step_4,
                        altext: "Zscaler ZPA API Key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Copy the client id">
                  <Typography variant={"body2"}>
                    In the new API row, copy the Client ID and paste it in the
                    form bellow.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zpa_step_5,
                        altext: "Zscaler ZPA Client ID",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Copy the customer id">
                  <Typography variant={"body2"}>
                    In the Public API page, click the "Copy Customer ID" button
                    at the top and paste it in the form bellow.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zpa_step_6,
                        altext: "Zscaler ZPA Customer ID",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the client id, client secret and customer id here">
                  <Box width={700}>
                    <ZscalerZpaConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default ZscalerZpaIntegration;
